/**
 * Main Certificate Search Application Class
 * Replaces Vue.js implementation with vanilla TypeScript
 */

import type { CertificateState, FormData, ValidationError, SearchResults, NormOption } from './types';
import { ApiService } from './services/ApiService';
import { ValidationService } from './services/ValidationService';
import { DomUtils } from './utils/DomUtils';

export class CertificateSearch {
    private state: CertificateState;
    private apiService: ApiService;

    // DOM element references
    private elements: {
        // Toggle buttons
        certWithBtn?: HTMLButtonElement;
        othersWithBtn?: HTMLButtonElement;

        // Form sections
        certNumberSection?: HTMLElement;
        advancedSection?: HTMLElement;

        // Input fields
        certNumberInput?: HTMLInputElement;
        nameInput?: HTMLInputElement;
        birthdateInput?: HTMLInputElement;
        companyInput?: HTMLInputElement;
        normSelect?: HTMLSelectElement;
        cityInput?: HTMLInputElement;
        countryInput?: HTMLInputElement;

        // Buttons
        searchBtn?: HTMLButtonElement;
        searchBtnLoading?: HTMLButtonElement;

        // Error containers
        certNrErrors?: HTMLElement;
        companyErrors?: HTMLElement;
        normErrors?: HTMLElement;
        countryErrors?: HTMLElement;

        // Results
        resultsContainer?: HTMLElement;
    } = {};

    constructor() {
        this.initializeState();
        this.validateEnvironment();
        this.apiService = new ApiService(window.app_urls);
        this.init();
    }

    /**
     * Initialize application state
     */
    private initializeState(): void {
        this.state = {
            input: {
                certificatenr: '',
                company: '',
                norm: '',
                city: '',
                country: '',
                name: '',
                birthdate: '',
            },
            results: {},
            translations: {},
            ui: {
                isLoading: false,
                isError: false,
                errors: [],
                searchMode: 'certificate',
                translationsLoaded: false,
            },
            norms: window.ts_norms || [],
        };
    }

    /**
     * Validate required global variables
     */
    private validateEnvironment(): void {
        if (!window.app_urls) {
            throw new Error('app_urls not defined. This app requires TYPO3 Frontend context.');
        }

        if (!ApiService.validateAppUrls(window.app_urls)) {
            throw new Error('Required app_urls properties missing (app_local_lang, app_search_url).');
        }

        if (!window.ts_norms) {
            // ts_norms not defined - norms dropdown will be empty
        }
    }

    /**
     * Initialize the application
     */
    private async init(): Promise<void> {
        try {
            // Load translations first
            await this.loadTranslations();

            // Cache DOM elements
            this.cacheElements();

            // Attach event listeners
            this.attachEventListeners();

            // Set initial search mode (certificate number)
            this.switchSearchMode('certificate');

            // Populate norms dropdown
            this.populateNorms();

            // Update translations in DOM
            this.updateTranslations();

            // Hide loading state
            this.hideLoadingState();
        } catch (error) {
            // Certificate Search initialization failed
            this.showGlobalError(`Initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Load translations from TYPO3
     */
    private async loadTranslations(): Promise<void> {
        try {
            const { translations, widgetArea } = await this.apiService.loadTranslations();
            this.state.translations = translations;
            this.state.ui.translationsLoaded = true;
        } catch (error) {
            // Failed to load translations - using fallbacks
            this.state.translations = {}; // Fallback to empty object
            this.state.ui.translationsLoaded = true;
            throw error;
        }
    }

    /**
     * Cache DOM element references
     */
    private cacheElements(): void {
        // Toggle buttons (now using SegmentButtonGroup with data attributes)
        this.elements.certWithBtn = document.querySelector('[data-tagcontentteaser-trigger="0"]') as HTMLButtonElement;
        this.elements.othersWithBtn = document.querySelector('[data-tagcontentteaser-trigger="1"]') as HTMLButtonElement;

        // Form sections
        this.elements.certNumberSection = DomUtils.getElementById('certNumberSearch');
        this.elements.advancedSection = DomUtils.getElementById('advancedSearch');

        // Input fields
        this.elements.certNumberInput = DomUtils.getElementById('txtCertNr') as HTMLInputElement;
        this.elements.nameInput = DomUtils.getElementById('txtName') as HTMLInputElement;
        this.elements.birthdateInput = DomUtils.getElementById('txtBirthdate') as HTMLInputElement;
        this.elements.companyInput = DomUtils.getElementById('txtCompany') as HTMLInputElement;
        this.elements.normSelect = DomUtils.getElementById('selectNorm') as HTMLSelectElement;
        this.elements.cityInput = DomUtils.getElementById('txtCity') as HTMLInputElement;
        this.elements.countryInput = DomUtils.getElementById('txtCountry') as HTMLInputElement;

        // Buttons
        this.elements.searchBtn = DomUtils.getElementById('qwsearchbtn') as HTMLButtonElement;
        this.elements.searchBtnLoading = DomUtils.getElementById('qwsearchbtn-loading') as HTMLButtonElement;

        // Error containers
        this.elements.certNrErrors = DomUtils.getElementById('certNrErrors');
        this.elements.companyErrors = DomUtils.getElementById('companyErrors');
        this.elements.normErrors = DomUtils.getElementById('normErrors');
        this.elements.countryErrors = DomUtils.getElementById('countryErrors');

        // Results
        this.elements.resultsContainer = DomUtils.getElementById('resultsContainer');
    }

    /**
     * Attach event listeners to DOM elements
     */
    private attachEventListeners(): void {
        // Toggle buttons
        this.elements.certWithBtn?.addEventListener('click', () => {
            this.switchSearchMode('certificate');
        });

        this.elements.othersWithBtn?.addEventListener('click', () => {
            this.switchSearchMode('advanced');
        });

        // Search button
        this.elements.searchBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleSearch();
        });

        // Form inputs - simulate v-model
        this.elements.certNumberInput?.addEventListener('input', (e) => {
            this.state.input.certificatenr = (e.target as HTMLInputElement).value;
        });

        this.elements.nameInput?.addEventListener('input', (e) => {
            this.state.input.name = (e.target as HTMLInputElement).value;
        });

        this.elements.birthdateInput?.addEventListener('input', (e) => {
            this.state.input.birthdate = (e.target as HTMLInputElement).value;
        });

        this.elements.companyInput?.addEventListener('input', (e) => {
            this.state.input.company = (e.target as HTMLInputElement).value;
        });

        this.elements.normSelect?.addEventListener('change', (e) => {
            this.state.input.norm = (e.target as HTMLSelectElement).value;
        });

        this.elements.cityInput?.addEventListener('input', (e) => {
            this.state.input.city = (e.target as HTMLInputElement).value;
        });

        this.elements.countryInput?.addEventListener('input', (e) => {
            this.state.input.country = (e.target as HTMLInputElement).value;
        });
    }

    /**
     * Populate norms dropdown from TypoScript configuration
     */
    private populateNorms(): void {
        if (!this.elements.normSelect || !Array.isArray(this.state.norms)) {
            return;
        }

        // Clear existing options except the first one
        DomUtils.clearSelectOptions(this.elements.normSelect, true);

        // Add norms from configuration
        this.state.norms.forEach((norm) => {
            DomUtils.appendOption(this.elements.normSelect!, norm.value, norm.label);
        });
    }

    /**
     * Update translations in existing DOM elements
     */
    private updateTranslations(): void {
        const elementsWithLangKeys = document.querySelectorAll('[data-lang-key]');
        elementsWithLangKeys.forEach((element) => {
            const langKey = element.getAttribute('data-lang-key');
            if (langKey) {
                const translation = this.state.translations[langKey];
                if (translation) {
                    element.textContent = translation;
                }
            }
        });
    }

    /**
     * Switch between certificate number and advanced search modes
     */
    private switchSearchMode(mode: 'certificate' | 'advanced'): void {
        this.state.ui.searchMode = mode;

        // Clear results
        this.clearResults();

        // Clear form inputs
        this.clearFormInputs();

        // Clear all errors
        this.clearAllErrors();

        // Update button states
        this.updateToggleButtons(mode);

        // Show/hide appropriate sections
        this.updateFormSections(mode);
    }

    /**
     * Update toggle button visual states (using tn-site SegmentButtonGroup pattern)
     */
    private updateToggleButtons(mode: 'certificate' | 'advanced'): void {
        // Remove is-active class from all buttons
        document.querySelectorAll('[data-tagcontentteaser-trigger]').forEach((btn) => {
            btn.classList.remove('is-active');
        });

        // Add is-active class to the selected button
        if (mode === 'certificate') {
            this.elements.certWithBtn?.classList.add('is-active');
        } else {
            this.elements.othersWithBtn?.classList.add('is-active');
        }
    }

    /**
     * Show/hide form sections based on search mode
     */
    private updateFormSections(mode: 'certificate' | 'advanced'): void {
        if (mode === 'certificate') {
            DomUtils.showElement(this.elements.certNumberSection);
            DomUtils.hideElement(this.elements.advancedSection);
        } else {
            DomUtils.hideElement(this.elements.certNumberSection);
            DomUtils.showElement(this.elements.advancedSection);
        }
    }

    /**
     * Handle search form submission
     */
    private async handleSearch(): Promise<void> {
        try {
            // Update state from DOM inputs
            this.updateStateFromInputs();

            // Set loading state
            this.setLoadingState(true);

            // Clear previous results and errors
            this.clearResults();
            this.clearAllErrors();

            // Validate form
            const validationErrors = ValidationService.validateForm(this.state.input, this.state.ui.searchMode, this.state.translations);

            if (ValidationService.hasErrors(validationErrors)) {
                this.displayValidationErrors(validationErrors);
                this.setLoadingState(false);
                return;
            }

            // Perform search
            let results: SearchResults;
            if (this.state.ui.searchMode === 'certificate') {
                results = await this.apiService.searchByCertificateNumber(this.state.input.certificatenr, this.state.input.name, this.state.input.birthdate);
            } else {
                results = await this.apiService.searchByAdvancedCriteria(
                    this.state.input.company,
                    this.state.input.norm,
                    this.state.input.city,
                    this.state.input.country
                );
            }

            // Display results
            this.displayResults(results);
        } catch (error) {
            // Search error occurred
            this.showSearchError(error instanceof Error ? error.message : 'Unknown search error');
        } finally {
            this.setLoadingState(false);
        }
    }

    /**
     * Update state from current DOM input values
     */
    private updateStateFromInputs(): void {
        this.state.input.certificatenr = DomUtils.getValue(this.elements.certNumberInput);
        this.state.input.name = DomUtils.getValue(this.elements.nameInput ?? null);
        this.state.input.birthdate = DomUtils.getValue(this.elements.birthdateInput ?? null);
        this.state.input.company = DomUtils.getValue(this.elements.companyInput);
        this.state.input.norm = DomUtils.getValue(this.elements.normSelect);
        this.state.input.city = DomUtils.getValue(this.elements.cityInput);
        this.state.input.country = DomUtils.getValue(this.elements.countryInput);
    }

    /**
     * Display validation errors in appropriate containers
     */
    private displayValidationErrors(errors: ValidationError[]): void {
        const errorsByField = ValidationService.groupErrorsByField(errors);

        // Display errors for each field
        Object.keys(errorsByField).forEach((field) => {
            const fieldErrors = errorsByField[field];
            const errorContainer = this.getErrorContainer(field);
            this.updateErrorDisplay(errorContainer, fieldErrors);
        });

        this.state.ui.isError = true;
        this.state.ui.errors = errors.map((e) => e.key);
    }

    /**
     * Get error container element for field
     */
    private getErrorContainer(field: string): HTMLElement | null {
        switch (field) {
            case 'certificatenr':
                return this.elements.certNrErrors || null;
            case 'company':
                return this.elements.companyErrors || null;
            case 'norm':
                return this.elements.normErrors || null;
            case 'country':
                return this.elements.countryErrors || null;
            case 'name':
                return document.getElementById('nameErrors');
            case 'birthdate':
                return document.getElementById('birthdateErrors');
            default:
                return null;
        }
    }

    /**
     * Update error display in container
     */
    private updateErrorDisplay(container: HTMLElement | null, errors: ValidationError[]): void {
        if (!container) return;

        if (errors.length > 0) {
            DomUtils.clearContent(container);

            errors.forEach((error) => {
                const errorDiv = document.createElement('div');
                const translatedMessage = this.state.translations[error.key] || error.message;
                errorDiv.textContent = translatedMessage;
                container.appendChild(errorDiv);
            });

            DomUtils.showElement(container);
        } else {
            DomUtils.hideElement(container);
        }
    }

    /**
     * Clear all error displays
     */
    private clearAllErrors(): void {
        const errorContainers = [this.elements.certNrErrors, this.elements.companyErrors, this.elements.normErrors, this.elements.countryErrors];

        errorContainers.forEach((container) => {
            this.updateErrorDisplay(container, []);
        });

        this.state.ui.isError = false;
        this.state.ui.errors = [];
    }

    /**
     * Display search results using Fluid template
     */
    private displayResults(results: SearchResults): void {
        this.state.results = results;

        if (results.error) {
            this.showSearchError(results.error);
            return;
        }

        this.renderResultsToFluidTemplate(results);
    }

    /**
     * Render search results to Fluid template
     */
    private renderResultsToFluidTemplate(results: SearchResults): void {
        // Get results from either CertData (API response) or list (legacy)
        const certificates = results.CertData || results.list || [];

        // Hide all result containers first
        this.hideAllResultContainers();

        if (!certificates || certificates.length === 0) {
            this.showNoResults();
            return;
        }

        this.showCertificateResults(certificates);
    }

    /**
     * Hide all result containers
     */
    private hideAllResultContainers(): void {
        const containers = ['certificateResults', 'noResults'];
        containers.forEach((id) => {
            const element = document.getElementById(id);
            if (element) {
                DomUtils.hideElement(element);
            }
        });
    }

    /**
     * Show no results message
     */
    private showNoResults(): void {
        const noResultsContainer = document.getElementById('noResults');
        if (noResultsContainer) {
            DomUtils.showElement(noResultsContainer);
            noResultsContainer.classList.remove('hidden');
        }

        // Hide the confirmation section
        const confirmationContainer = document.getElementById('certificateConfirmation');
        if (confirmationContainer) {
            confirmationContainer.classList.add('hidden');
        }
    }

    /**
     * Show certificate results
     */
    private showCertificateResults(certificates: any[]): void {
        const resultsContainer = document.getElementById('certificateResults');
        const itemsContainer = document.getElementById('certificateItems');

        if (!resultsContainer || !itemsContainer) {
            // Certificate results containers not found
            return;
        }

        // Clear existing certificates (but keep the template!)
        const existingResults = itemsContainer.querySelectorAll('.certificate-item:not([data-template="true"])');
        existingResults.forEach((item) => item.remove());

        // Create certificate items from template
        certificates.forEach((cert) => {
            this.createCertificateItem(cert, itemsContainer);
        });

        // Show the results container
        DomUtils.showElement(resultsContainer);
        resultsContainer.classList.remove('hidden');

        // Show the confirmation section
        const confirmationContainer = document.getElementById('certificateConfirmation');
        if (confirmationContainer) {
            confirmationContainer.classList.remove('hidden');
        }
    }

    /**
     * Create certificate item from Fluid template
     */
    private createCertificateItem(cert: any, container: HTMLElement): void {
        // Find the template - it should be pre-rendered by Fluid
        const template = container.querySelector('.certificate-item[data-template="true"]') as HTMLElement;

        if (!template) {
            // Certificate template not found - Fluid template should have rendered it
            return;
        }

        // Clone the template
        const item = template.cloneNode(true) as HTMLElement;
        item.removeAttribute('data-template');
        item.classList.remove('hidden'); // Remove hidden class instead of inline style

        this.populateCertificateItem(item, cert);

        // Add to container
        container.appendChild(item);
    }

    /**
     * Populate certificate item with data using token replacement
     */
    private populateCertificateItem(item: HTMLElement, cert: any): void {
        // Extract certificate data - corrected field mappings
        const certificateNumber = cert.tplnr || cert.certificate_number || '';
        const validUntil = this.formatDate(cert.gueltigBis || cert.gubis || cert.valid_until || '');
        const normText = cert.normText || cert.normCode || cert.norm || cert.norm_text || '';
        const companyName = cert.name1 || cert.firma || cert.company || '';
        const companyName2 = cert.name2 || cert.firma2 || cert.company2 || '';
        const companyName3 = cert.name3 || cert.firma3 || cert.company3 || '';
        const street = cert.street || cert.strasse || '';
        const zipCode = cert.postCode1 || cert.plz || cert.zip || '';
        const city = cert.city1 || cert.ort || cert.city || '';
        const country = cert.country || cert.land || '';
        const email = cert.email || 'info@tuev-nord.de';

        // Cobra-spezifisches Personenfeld (Geburtsdatum)
        const birthdate = this.formatDate(cert.birthdate || '');

        // Create token replacement map
        const tokenMap = {
            '[CERT_NUMBER]': certificateNumber,
            '[VALID_UNTIL]': validUntil,
            '[NORM_TEXT]': normText,
            '[COMPANY_NAME]': companyName,
            '[COMPANY_NAME2]': companyName2,
            '[COMPANY_NAME3]': companyName3,
            '[STREET]': street,
            '[ZIP_CODE]': zipCode,
            '[CITY]': city,
            '[COUNTRY]': country,
            '[EMAIL]': email,
            '[BIRTHDATE]': birthdate,
        };

        // Replace all tokens in the item - Direct DOM approach for debugging

        // Try direct selector replacement first
        const certNumberEl = item.querySelector('.certificate-number');
        if (certNumberEl) {
            certNumberEl.textContent = certificateNumber;
        }

        const normTextEl = item.querySelector('.norm-text');
        if (normTextEl) {
            normTextEl.textContent = normText;
        }

        const validUntilEl = item.querySelector('.valid-until');
        if (validUntilEl) {
            validUntilEl.textContent = validUntil;
        }

        const companyEl = item.querySelector('.company-primary');
        if (companyEl) {
            companyEl.textContent = companyName;
        }

        // Address elements
        const streetEl = item.querySelector('.street');
        if (streetEl) {
            streetEl.textContent = street;
        }

        const zipEl = item.querySelector('.zip-code');
        if (zipEl) {
            zipEl.textContent = zipCode;
        }

        const cityEl = item.querySelector('.city');
        if (cityEl) {
            cityEl.textContent = city;
        }

        const countryEl = item.querySelector('.country');
        if (countryEl) {
            countryEl.textContent = country;
        }

        const birthdateEl = item.querySelector('.birthdate');
        if (birthdateEl) {
            birthdateEl.textContent = birthdate;
        }

        // Also try the token replacement method
        this.replaceTokensInElement(item, tokenMap);

        // Show/hide optional rows based on data availability (5-column layout)
        this.setOptionalRowVisibility(item, '.company-secondary', companyName2);
        this.setOptionalRowVisibility(item, '.company-tertiary', companyName3);
        this.setOptionalRowVisibility(item, '.street-row', street);
        this.setOptionalRowVisibility(item, '.zip-city-row', zipCode || city); // Show if either zip or city exists
        this.setOptionalRowVisibility(item, '.country-row', country);

        // No email links or buttons in this layout
    }

    /**
     * Show certificate details (placeholder for future implementation)
     */
    private showCertificateDetails(cert: any): void {
        // Placeholder for showing detailed certificate information
        // Could open a modal, redirect to details page, etc.
    }

    /**
     * Replace tokens in all text nodes of an element
     */
    private replaceTokensInElement(element: Element, tokenMap: { [key: string]: string }): void {
        // Walk through all nodes
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

        const textNodes: Text[] = [];
        let node;

        // Collect all text nodes
        while ((node = walker.nextNode())) {
            textNodes.push(node as Text);
        }

        // Replace tokens in each text node
        textNodes.forEach((textNode) => {
            let text = textNode.nodeValue || '';

            // Replace each token
            Object.keys(tokenMap).forEach((token) => {
                text = text.replace(new RegExp(this.escapeRegExp(token), 'g'), tokenMap[token]);
            });

            if (text !== textNode.nodeValue) {
                textNode.nodeValue = text;
            }
        });
    }

    /**
     * Escape string for use in RegExp
     */
    private escapeRegExp(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Set visibility of optional row using Tailwind classes
     */
    private setOptionalRowVisibility(item: HTMLElement, selector: string, value: string): void {
        const row = item.querySelector(selector) as HTMLElement;
        if (row) {
            if (value && value.trim()) {
                row.classList.remove('hidden');
            } else {
                row.classList.add('hidden');
            }
        }
    }

    /**
     * Set text content of element
     */
    private setElementText(container: HTMLElement, selector: string, text: string): void {
        const element = container.querySelector(selector) as HTMLElement;
        if (element) {
            element.textContent = text;
        }
    }

    /**
     * Set optional field and show/hide container
     */
    private setOptionalField(container: HTMLElement, selector: string, text: string): void {
        const element = container.querySelector(selector) as HTMLElement;
        if (element) {
            if (text && text.trim().length > 0) {
                element.textContent = text;
                DomUtils.showElement(element);
            } else {
                DomUtils.hideElement(element);
            }
        }
    }

    /**
     * Set optional row and show/hide entire row
     */
    private setOptionalRow(container: HTMLElement, rowSelector: string, textSelector: string, text: string): void {
        const row = container.querySelector(rowSelector) as HTMLElement;
        const textElement = container.querySelector(textSelector) as HTMLElement;

        if (row && textElement) {
            if (text && text.trim().length > 0) {
                textElement.textContent = text;
                DomUtils.showElement(row);
            } else {
                DomUtils.hideElement(row);
            }
        }
    }

    /**
     * Format date from YYYYMMDD to DD.MM.YYYY
     */
    private formatDate(dateString: string): string {
        if (!dateString || dateString.length !== 8) {
            return dateString || '';
        }

        const year = dateString.substr(0, 4);
        const month = dateString.substr(4, 2);
        const day = dateString.substr(6, 2);

        return `${day}.${month}.${year}`;
    }

    /**
     * Clear search results
     */
    private clearResults(): void {
        this.state.results = {};
        this.hideAllResultContainers();
    }

    /**
     * Clear all form inputs
     */
    private clearFormInputs(): void {
        // Clear state
        this.state.input = {
            certificatenr: '',
            company: '',
            norm: '',
            city: '',
            country: '',
            name: '',
            birthdate: '',
        };

        // Clear DOM inputs
        DomUtils.setValue(this.elements.certNumberInput, '');
        DomUtils.setValue(this.elements.companyInput, '');
        DomUtils.setValue(this.elements.normSelect, '');
        DomUtils.setValue(this.elements.cityInput, '');
        DomUtils.setValue(this.elements.countryInput, '');
    }

    /**
     * Set loading state
     */
    private setLoadingState(isLoading: boolean): void {
        this.state.ui.isLoading = isLoading;

        if (isLoading) {
            DomUtils.hideElement(this.elements.searchBtn);
            DomUtils.showElement(this.elements.searchBtnLoading);
        } else {
            DomUtils.showElement(this.elements.searchBtn);
            DomUtils.hideElement(this.elements.searchBtnLoading);
        }
    }

    /**
     * Show search-specific error
     */
    private showSearchError(message: string): void {
        const errorContainer = this.state.ui.searchMode === 'certificate' ? this.elements.certNrErrors : this.elements.companyErrors;

        if (errorContainer) {
            const errorDiv = document.createElement('div');
            errorDiv.textContent = message;
            DomUtils.clearContent(errorContainer);
            errorContainer.appendChild(errorDiv);
            DomUtils.showElement(errorContainer);
        }

        this.state.ui.isError = true;
        this.state.ui.errors = [message];
    }

    /**
     * Show global application error
     */
    private showGlobalError(message: string): void {
        const errorEl = document.querySelector('.certificate-search__error');
        const errorDetailsEl = document.querySelector('.certificate-search__error-details');

        if (errorEl) {
            DomUtils.showElement(errorEl as HTMLElement);
        }

        if (errorDetailsEl) {
            errorDetailsEl.textContent = message;
        }
    }

    /**
     * Hide loading state when app initializes
     */
    private hideLoadingState(): void {
        const loadingEl = document.querySelector('.certificate-search__loading');
        if (loadingEl) {
            DomUtils.hideElement(loadingEl as HTMLElement);
        }
    }
}
